import {
  Dispatch, SetStateAction, useCallback, useEffect, useState,
} from 'react';
import { refreshPeriodDates } from '../../utils/dateUtils';
import { getLocalStorageKey } from '../../utils/storageUtils';
import useGetFleetId from './useGetFleetId';

type GroupFilters = Record<string, string[]>;

function readGroupFilters(storageKey: string): GroupFilters {
  if (!storageKey) {
    return {};
  }

  try {
    const stored = localStorage.getItem(storageKey);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
}

export function useGroupFilters(): [GroupFilters, Dispatch<SetStateAction<GroupFilters>>] {
  const fleetId = useGetFleetId();
  const storageKey = fleetId ? getLocalStorageKey(`selected-groups-${fleetId}`) : '';
  const [groupFilters, setGroupFiltersState] = useState<GroupFilters>(() => readGroupFilters(storageKey));

  useEffect(() => {
    setGroupFiltersState(readGroupFilters(storageKey));
  }, [storageKey]);

  const setGroupFilters = useCallback<Dispatch<SetStateAction<GroupFilters>>>((value) => {
    setGroupFiltersState((prev) => {
      const next = typeof value === 'function' ? value(prev) : value;

      if (storageKey) {
        try {
          localStorage.setItem(storageKey, JSON.stringify(next));
        } catch (error) {
          console.error('Error writing group filters to localStorage', error);
        }
      }

      return next;
    });
  }, [storageKey]);

  return [groupFilters, setGroupFilters];
}

// Custom hook to manage localStorage
function useLocalStorage(key: string, initialValue?: any):any {
  // Get stored value from localStorage or use initialValue
  const getStoredValue = ():any => {
    try {
      const stored = localStorage.getItem(key);
      if (stored !== null) return refreshPeriodDates(JSON.parse(stored));
      return initialValue;
    } catch (error) {
      console.error('Error reading localStorage', error);
      return initialValue;
    }
  };

  const [storedValue, setStoredValue] = useState(() => getStoredValue());

  useEffect(() => {
    // Update localStorage whenever storedValue changes
    try {
      localStorage.setItem(key, JSON.stringify(storedValue));
    } catch (error) {
      console.error('Error writing to localStorage', error);
    }
  }, [key, storedValue]);

  // Return the stored value and a function to update it
  return [storedValue, setStoredValue];
}

export default useLocalStorage;
